import React, { useState, useEffect } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Snackbar, Alert
} from "@mui/material";
import { collection, doc, setDoc, query, where, onSnapshot, getDocs, deleteDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../firebase";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

const AddCategory = ({ companyCode, unitCode }) => {
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
    const [searchText, setSearchText] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [newCategory, setNewCategory] = useState({
    name: "",
    unitCode: ""
  });

const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(/(\s|\/|-)/) // split by space, slash, hyphen (keeps separators)
    .map(word => {
      if (word === " " || word === "/" || word === "-") return word;
      return word ? word.charAt(0).toUpperCase() + word.slice(1) : "";
    })
    .join("");
};

const normalizeCategoryName = (value) => {
  return toTitleCase(
    value
      .replace(/^\s+/, "")   // remove starting spaces
      .replace(/\s{2,}/g, " ") // allow only one space between words

  );
};

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Fetch Units
useEffect(() => {
  const fetchUnits = async () => {
    if (!companyCode || !unitCode) return;

    const q = query(
      collection(db, "units"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode) 
    );

    const snap = await getDocs(q);

    const list = snap.docs.map(doc => ({
      unitCode: doc.id,
      name: doc.data().unitName
    }));

    setUnits(list);
  };

  fetchUnits();
}, [companyCode, unitCode]);

  // Fetch Categories
  useEffect(() => {
  if (!companyCode) return;

  const q = query(
    collection(db, "categories"),
    where("companyCode", "==", companyCode),
   
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setCategories(list);
  });

  return () => unsub();
}, [companyCode]);

  // Add Category
  const handleAdd = async () => {
  if (!newCategory.name || !newCategory.unitCode) {
    alert("Please fill all fields");
    return;
  }

  const formattedName = toTitleCase(newCategory.name).trim();

  // Check duplicate
  const q = query(
    collection(db, "categories"),
    where("companyCode", "==", companyCode),
    where("unitCode", "==", newCategory.unitCode),
    where("name", "==", formattedName)
  );

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    setSnackbar({
      open: true,
      message: `Category "${formattedName}" already exists`,
      severity: "error"
    });
    return;
  }

  const docRef = doc(collection(db, "categories"));

  await setDoc(docRef, {
    id: docRef.id,
    name: formattedName,
    unitCode: newCategory.unitCode,
    companyCode,
    createdAt: new Date().toISOString(),
    createdBy: auth.currentUser?.uid,
    modifiedAt: new Date().toISOString(),
    modifiedBy: auth.currentUser?.uid
  });

  setOpenAdd(false);
  setNewCategory({ name: "", unitCode: "" });

  setSnackbar({ open: true, message: "Category added successfully!", severity: "success" });
};

  // Update Category
 const handleUpdate = async () => {
  const formattedName = toTitleCase(selectedCategory.name).trim();

  // Check duplicate (exclude current doc)
  const q = query(
    collection(db, "categories"),
    where("companyCode", "==", companyCode),
    where("unitCode", "==", selectedCategory.unitCode),
    where("name", "==", formattedName)
  );

  const snapshot = await getDocs(q);

  const isDuplicate = snapshot.docs.some(doc => doc.id !== selectedCategory.id);

  if (isDuplicate) {
    setSnackbar({
      open: true,
      message: `Category "${formattedName}" already exists`,
      severity: "error"
    });
    return;
  }

  const docRef = doc(db, "categories", selectedCategory.id);

  await updateDoc(docRef, {
    name: formattedName,
    unitCode: selectedCategory.unitCode,
    modifiedAt: new Date().toISOString(),
    modifiedBy: auth.currentUser?.uid
  });

  setOpenEdit(false);
  setSnackbar({ open: true, message: "Category updated successfully!", severity: "success" });
};
  // Delete Category
 /* const handleDelete = async () => {
    await deleteDoc(doc(db, "categories", itemToDelete.id));

    setOpenDelete(false);
    setSnackbar({ open: true, message: "Category deleted successfully!", severity: "success" });
  };*/
  const handleDelete = async () => {
  try {
    // Check if any menu item exists for this category
    const menuQuery = query(
      collection(db, "companymenu"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", itemToDelete.unitCode),
      where("category", "==", itemToDelete.name)
    );

    const menuSnapshot = await getDocs(menuQuery);

    if (!menuSnapshot.empty) {
      setSnackbar({
        open: true,
        message: `Cannot delete category "${itemToDelete.name}" because menu items exist under this category.`,
        severity: "error",
      });

      setOpenDelete(false);
      return;
    }

    await deleteDoc(doc(db, "categories", itemToDelete.id));

    setOpenDelete(false);

    setSnackbar({
      open: true,
      message: "Category deleted successfully!",
      severity: "success",
    });

  } catch (error) {
    console.error("Error deleting category:", error);

    setSnackbar({
      open: true,
      message: "Failed to delete category.",
      severity: "error",
    });
  }
};

  const getUnitName = (code) => {
    const u = units.find(x => x.unitCode === code);
    return u ? u.name : "N/A";
  };

  const filteredCategories = categories.filter(cat =>
  cat.name.toLowerCase().includes(searchText.toLowerCase())
);

  return (
    <Box p={4}>
      <Box
  display="flex"
  justifyContent="space-between"
  alignItems="center"
>
  <Typography variant="h4">Category Master</Typography>

  <Button
    variant="contained"
    //onClick={() => setOpenAdd(true)}
     onClick={() => {
    setNewCategory({
      name: "",
      unitCode: ""
    });
    setOpenAdd(true);
  }}
  >
    Add Category
  </Button>
</Box>
<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 3,
    mt: 2
  }}
>
  
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <Typography
      variant="h5"
      sx={{ fontWeight: "bold", color: "text.primary" }}
    >
      Category List
    </Typography>

    <TextField
      label="Search By Category Name"
      variant="outlined"
      size="small"
      sx={{ width: 250 }}   
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  </Box>
</Box>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
  {filteredCategories.length === 0 ? (
    <TableRow>
      <TableCell colSpan={3} align="center">
        <Typography variant="body1" color="text.secondary">
          No categories found 
        </Typography>
      </TableCell>
    </TableRow>
  ) : (
    filteredCategories.map(cat => (
      <TableRow key={cat.id}>
        <TableCell>{cat.name}</TableCell>
        <TableCell>{getUnitName(cat.unitCode)}</TableCell>
        <TableCell>
          <IconButton 
            color="primary"
            onClick={() => {
              setSelectedCategory(cat);
              setOpenEdit(true);
            }}
          >
            <EditIcon />
          </IconButton>

          <IconButton 
            color="error"
            onClick={() => {
              setItemToDelete(cat);
              setOpenDelete(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    ))
  )}
</TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            fullWidth
            margin="dense"
            value={newCategory.name}
            onChange={(e) =>
            setNewCategory({
                ...newCategory,
                //name: toTitleCase(e.target.value)
                 name: normalizeCategoryName(e.target.value)
            })
            }
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 160 }}>
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
            labelId="unit-select-label"
              name="unitCode"
              label="Unit"
              required
              value={newCategory.unitCode}
              onChange={(e) =>
                setNewCategory({ ...newCategory, unitCode: e.target.value })
              }
            >
              {units.map(u => (
                <MenuItem key={u.unitCode} value={u.unitCode}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button onClick={handleAdd}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            fullWidth
            margin="dense"
            value={selectedCategory?.name || ""}
            onChange={(e) =>
            setSelectedCategory({
                ...selectedCategory,
                //name: toTitleCase(e.target.value)
                 name: normalizeCategoryName(e.target.value)
            })
            }
          />

          <FormControl fullWidth margin="dense" sx={{ mt: 2, minWidth: 160 }}>
            <InputLabel id="unit-select-label">Unit</InputLabel>
          <Select
            fullWidth
            labelId="unit-select-label"
            name="unitCode"
            label="Unit"
            value={selectedCategory?.unitCode || ""}
            onChange={(e) =>
              setSelectedCategory({
                ...selectedCategory,
                unitCode: e.target.value
              })
            }
          >
            {units.map(u => (
              <MenuItem key={u.unitCode} value={u.unitCode}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleUpdate}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}  maxWidth="xs"
  fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
         <DialogContent>
            <Typography>
              Are you sure you want to delete "{itemToDelete?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
      <Alert
        severity={snackbar.severity}
        variant="outlined"
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          border: "1px solid #ddd",
          fontWeight: "bold",
          fontSize: "16px"
        }}
      >
        {snackbar.message}
      </Alert>
      </Snackbar>

    </Box>
  );
};

export default AddCategory;